import {
  DEFAULT_LETTER_TEMPLATES,
  LETTER_SAMPLES_VERSION,
} from "./defaultLetterTemplates";

export const LETTER_TEMPLATES_STORAGE_KEY = "pacehrm_letter_templates";
export const APPOINTMENT_LETTER_TYPE = "appointment";
export const LETTER_TEMPLATES_UPDATED_EVENT = "pacehrm:letter-templates-updated";

function isSystemSampleId(id) {
  return typeof id === "string" && /^[a-z0-9-]+-\d+$/i.test(id);
}

function shouldRefreshSystemSample(stored, def) {
  if (!def || !isSystemSampleId(stored?.id)) return false;
  if (stored.id !== def.id) return false;
  const storedVer = Number(stored.sampleVersion || 0);
  const defVer = Number(def.sampleVersion || LETTER_SAMPLES_VERSION || 0);
  if (storedVer < defVer) return true;
  // Legacy samples without salary table / professional structure
  const content = String(stored.content || "");
  if (!(content.includes("salary_structure_table") || content.includes("salary_structure_text"))) {
    if (String(def.content || "").includes("salary_structure")) return true;
  }
  if (content.includes("Component tags you can also use")) return true;
  return false;
}

export function loadLetterTemplates(fallback = DEFAULT_LETTER_TEMPLATES) {
  const cloneFallback = () =>
    typeof structuredClone === "function"
      ? structuredClone(fallback)
      : JSON.parse(JSON.stringify(fallback));

  try {
    const raw = localStorage.getItem(LETTER_TEMPLATES_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        // Merge defaults with stored templates; refresh outdated system samples only.
        const merged = cloneFallback();
        Object.keys(merged).forEach((type) => {
          const storedList = Array.isArray(parsed[type]) ? parsed[type] : [];
          const defaultList = Array.isArray(merged[type]) ? merged[type] : [];
          const byId = new Map();
          defaultList.forEach((t) => byId.set(t.id, t));
          storedList.forEach((t) => {
            const { fileBase64, ...safeT } = t || {};
            const def = defaultList.find((d) => d.id === safeT.id);
            if (def && shouldRefreshSystemSample(safeT, def)) {
              byId.set(safeT.id, { ...safeT, ...def, sampleVersion: def.sampleVersion });
            } else if (def && isSystemSampleId(safeT.id) && Number(safeT.sampleVersion) >= Number(def.sampleVersion)) {
              byId.set(safeT.id, { ...def, ...safeT, sampleVersion: def.sampleVersion });
            } else {
              byId.set(safeT.id, safeT);
            }
          });
          merged[type] = Array.from(byId.values());
        });
        // Keep any custom letter types from storage
        Object.keys(parsed).forEach((type) => {
          if (merged[type] == null && Array.isArray(parsed[type])) {
            merged[type] = parsed[type];
          }
        });
        return merged;
      }
    }
  } catch (error) {
    console.error("Failed to load letter templates from storage:", error);
  }
  return cloneFallback();
}

export function saveLetterTemplates(templates) {
  try {
    // Never persist Word fileBase64 blobs — they freeze the UI via localStorage.
    const light = {};
    Object.entries(templates || {}).forEach(([type, list]) => {
      light[type] = (Array.isArray(list) ? list : []).map((t) => {
        if (!t || typeof t !== "object") return t;
        const { fileBase64, ...rest } = t;
        return rest;
      });
    });
    localStorage.setItem(LETTER_TEMPLATES_STORAGE_KEY, JSON.stringify(light));
    window.dispatchEvent(new CustomEvent(LETTER_TEMPLATES_UPDATED_EVENT));
  } catch (error) {
    console.error("Failed to save letter templates to storage:", error);
  }
}

export function subscribeLetterTemplatesUpdated(callback) {
  const onCustom = () => callback();
  const onStorage = (event) => {
    if (event.key === LETTER_TEMPLATES_STORAGE_KEY) callback();
  };
  window.addEventListener(LETTER_TEMPLATES_UPDATED_EVENT, onCustom);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(LETTER_TEMPLATES_UPDATED_EVENT, onCustom);
    window.removeEventListener("storage", onStorage);
  };
}

export function getTemplatesByType(typeId) {
  const stored = loadLetterTemplates();
  const list = stored?.[typeId];
  if (Array.isArray(list)) return list;
  const fallback = DEFAULT_LETTER_TEMPLATES?.[typeId];
  return Array.isArray(fallback) ? fallback : [];
}

export function getLetterTemplateSelectOptions(
  typeId,
  { includeEmpty = true, emptyLabel = "Select letter template" } = {}
) {
  const templates = getTemplatesByType(typeId);
  const options = templates.map((template) => ({
    value: template.id,
    label: template.name || template.id,
  }));
  return includeEmpty ? [{ value: "", label: emptyLabel }, ...options] : options;
}

export function getLetterTemplateLabel(typeId, templateId) {
  if (!templateId) return "—";
  const found = getTemplatesByType(typeId).find((template) => template.id === templateId);
  return found?.name || templateId;
}
